import { FC } from 'react';
import { shallow } from 'zustand/shallow';

import { StyledTitle } from '../Title/Title.styles';
import { useMixedInventoryStore } from '../../store/mixedInventoryStore';
import { useMiInventoryEnabled } from '../../utils';
import { MiRoomTitleProps } from './MiRoomTitle.types';

/**
 * MiRoomTitle — store-aware title atom for the MI (Mixed Inventory) RLM page.
 *
 * Handles both AEM title nodes that live on the RLM page, identified by the
 * `customClassName` AEM authors apply:
 *
 *   mi-static-navigation-title  → "Select a Room and Rate"
 *     Visible when MI is disabled OR totalRooms === 1.
 *     Hidden (returns null) when MI is enabled AND totalRooms > 1.
 *
 *   mi-dynamic-navigation-title → "Select Room X"
 *     Visible ONLY when MI is enabled AND totalRooms > 1.
 *     Hidden (returns null) in all other cases.
 *     The literal placeholder "X" is replaced with currentRoomIndex via
 *     regex /\bX\b/g so the title reads "Select Room 1", "Select Room 2", etc.
 *     currentRoomIndex advances in the store as the user progresses through
 *     each sequential room selection (goToNextRoom / incrementRoomProgress).
 *
 * Both nodes return null when totalRooms <= 0 (store not yet seeded from session).
 *
 * A single shallow selector avoids two independent store subscriptions and
 * the Zustand tearing / double re-render risk.
 *
 * No rate-selection, cart-creation, or flyout logic — display only.
 */
export const MiRoomTitle: FC<MiRoomTitleProps> = ({
  text = '',
  type = 'h1',
  customClassName,
  testId = 'mi-room-title',
}: MiRoomTitleProps) => {
  const isMiEnabled = useMiInventoryEnabled();

  const { totalRooms, currentRoomIndex } = useMixedInventoryStore(
    state => ({
      totalRooms: state.searchCriteria.totalRooms,
      currentRoomIndex: state.roomProgression.currentRoomIndex,
    }),
    shallow
  );

  const isStaticTitle = customClassName?.includes('mi-static-navigation-title') ?? false;
  const isDynamicTitle = customClassName?.includes('mi-dynamic-navigation-title') ?? false;

  // Guard: only handle known MI title variants
  if (!isStaticTitle && !isDynamicTitle) return null;

  // Guard: store not yet seeded — totalRooms is 0 before initializeMixedInventoryStore runs
  if (totalRooms <= 0) return null;

  // Dynamic title ("Select Room X"): only visible when MI is enabled AND booking is multi-room
  if (isDynamicTitle && !(isMiEnabled && totalRooms > 1)) return null;

  // Static title ("Select a Room and Rate"): visible for single-room OR when MI is disabled
  // Hidden only when MI is enabled AND user has selected more than 1 room
  if (isStaticTitle && isMiEnabled && totalRooms > 1) return null;

  const Tag = type as keyof JSX.IntrinsicElements;
  // Replace the AEM placeholder "X" (whole word only) with the live room index
  // e.g. "Select Room X" + currentRoomIndex 2 → "Select Room 2"
  const titleText = isDynamicTitle
    ? text.replace(/\bX\b/g, String(currentRoomIndex))
    : text;

  return (
    <StyledTitle
      as={Tag}
      data-component-name="a-book-MiRoomTitle"
      data-testid={testId}
      className={`t-font-weight-m ${customClassName}`}
    >
      {titleText}
    </StyledTitle>
  );
};
